import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  WhatsApp,
  Send,
  AccessTime,
  SupportAgent,
} from '@mui/icons-material';
import { HeaderV2, FooterV2 } from '../../components/layout';
import { colors } from '../../components/theme/colors';

const ContactPageV2 = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate submission
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 600);
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent('Hi Rehman Travels, I would like to inquire about travel packages and services.');
    window.open(`https://wa.me/923000778844?text=${text}`, '_blank');
  };

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <HeaderV2 />

      {/* Hero Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0b2239 0%, #1e4d79 100%)',
          color: '#fff',
          pt: { xs: 12, md: 14 },
          pb: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 1.5 }}>
            24/7 CUSTOMER SUPPORT
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, mb: 2, fontFamily: "'Fraunces', Georgia, serif" }}>
            Contact Us
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.88, maxWidth: 600, mx: 'auto', fontSize: '1.1rem' }}>
            Have a question about an Umrah package, custom itinerary, or flight quote? Our dedicated travel specialists are here to assist you.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flexGrow: 1 }}>
        <Grid container spacing={4}>
          {/* Left Column: Contact Cards & Info */}
          <Grid item xs={12} md={5}>
            <Stack spacing={2.5}>
              {/* WhatsApp Live Agent Card */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 2.5,
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  color: '#fff',
                  boxShadow: '0 8px 24px rgba(37, 211, 102, 0.25)',
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                    <WhatsApp sx={{ fontSize: 36 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Instant WhatsApp Chat
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Fastest response from our visa & ticketing team
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={openWhatsApp}
                    sx={{
                      backgroundColor: '#fff',
                      color: '#128C7E',
                      fontWeight: 700,
                      mt: 1,
                      '&:hover': { backgroundColor: '#f0fdf4' },
                    }}
                  >
                    Chat on WhatsApp (+92 300 0778844)
                  </Button>
                </CardContent>
              </Card>

              {/* Phone & Email */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${colors.border.light}` }}>
                <Stack spacing={3}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Phone sx={{ color: 'primary.main', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                        Call Helpline
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        +92 51 111 786 785
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Mobile: +92 300 0778844
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Email sx={{ color: 'primary.main', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                        Email Us
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        info@rehmantravels.com
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        support@rehmantravels.com
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <AccessTime sx={{ color: 'primary.main', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                        Operating Hours
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        Mon – Sat: 9:00 AM – 9:00 PM
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Sunday: 11:00 AM – 6:00 PM (Emergency Desk 24/7)
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>

              {/* Branch Offices */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${colors.border.light}` }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn color="primary" /> Branch Offices
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Islamabad Head Office</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fazal-ul-Haq Road, Blue Area, Islamabad, Pakistan
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Lahore Branch</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Main Boulevard, Gulberg III, Lahore, Pakistan
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          {/* Right Column: Send a Message Form */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: `1px solid ${colors.border.light}`,
              }}
            >
              <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                Send Us a Message
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Fill out the form below and an Umrah or holiday advisor will get back to you within 30 minutes.
              </Typography>

              {submitted && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSubmitted(false)}>
                  Thank you! Your message has been sent successfully. One of our specialists will reach out to you shortly.
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="0300 1234567"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject / Topic"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="e.g. 14 Days Family Umrah Inquiry"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message / Requirements"
                      name="message"
                      multiline
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your travel dates, number of passengers, and preferred hotel categories..."
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={<Send />}
                      sx={{ py: 1.5, px: 4, fontWeight: 700 }}
                    >
                      {loading ? 'Sending...' : 'Submit Inquiry'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <FooterV2 />
    </Box>
  );
};

export default ContactPageV2;
